import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOSchema from '../components/SEOSchema';
import { clientReviews } from '../data/mock';
import { Star, Quote, ShieldCheck, Activity, ArrowRight, Stethoscope } from 'lucide-react';

// Maps a review's genuine `program` label to a visitor-friendly filter category.
const categoryForProgram = (program = '') => {
  const p = program.toLowerCase();
  if (p.includes('prediabetes')) return 'Prediabetes';
  if (p.includes('diabetes') || p.includes('couple') || p.includes('elders')) return 'Diabetes';
  return 'Weight & Fitness';
};

const StarRating = ({ rating }) => (
  <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`} data-testid="review-star-rating">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i <= rating ? 'text-amber-400 fill-amber-400' : 'text-slate-600'}`}
        aria-hidden="true"
      />
    ))}
  </div>
);

const ReviewCard = ({ review, index }) => (
  <article
    className="h-full flex flex-col bg-slate-900/50 border border-slate-800 rounded-3xl p-6 sm:p-8 hover:border-slate-700 transition-colors"
    data-testid={`review-card-${index}`}
  >
    <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
      <StarRating rating={review.rating} />
      {review.program && (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 text-xs font-medium">
          <Stethoscope className="w-3.5 h-3.5" aria-hidden="true" />
          {review.program}
        </span>
      )}
    </div>

    <h2 className="text-lg sm:text-xl font-bold text-white mb-4 leading-snug">{review.title}</h2>

    {review.highlights && (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {review.highlights.map((h, i) => (
          <div key={i} className="bg-slate-800/50 border border-slate-700/60 rounded-xl px-3 py-2.5 text-center">
            <div className="text-emerald-400 font-bold text-sm sm:text-base">{h.value}</div>
            <div className="text-slate-400 text-[11px] mt-0.5 leading-tight">{h.label}</div>
          </div>
        ))}
      </div>
    )}

    <div className="relative flex-1">
      <Quote className="w-7 h-7 text-blue-500/20 absolute -top-1 -left-1" aria-hidden="true" />
      <div className="space-y-3 pl-6">
        {review.text.map((para, i) => (
          <p key={i} className="text-slate-300 text-sm sm:text-base leading-relaxed">{para}</p>
        ))}
      </div>
    </div>

    {review.clinicalProgress && (
      <div className="mt-6 bg-slate-800/40 border border-slate-700/60 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-emerald-400" aria-hidden="true" />
          <h3 className="text-white font-semibold text-sm">Clinical Progress</h3>
        </div>
        <dl className="space-y-2.5">
          {review.clinicalProgress.map((item, i) => (
            <div key={i} className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-4">
              <dt className="text-slate-400 text-xs sm:text-sm">{item.label}</dt>
              <dd className="text-slate-200 text-xs sm:text-sm font-medium sm:text-right">{item.value}</dd>
            </div>
          ))}
        </dl>
        {review.clinicalNote && (
          <p className="text-slate-500 text-[11px] mt-3 italic">{review.clinicalNote}</p>
        )}
      </div>
    )}

    <div className="mt-6 pt-4 border-t border-slate-800 flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-cyan-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
        {review.name?.charAt(0) || 'C'}
      </div>
      <div>
        <div className="text-white font-semibold text-sm">{review.name}</div>
        {review.context && <div className="text-slate-400 text-xs">{review.context}</div>}
      </div>
    </div>
  </article>
);

const CATEGORIES = ['All', 'Diabetes', 'Prediabetes', 'Weight & Fitness'];

const ReviewsPage = () => {
  const [active, setActive] = useState('All');
  const filtered = useMemo(
    () => (active === 'All' ? clientReviews : clientReviews.filter((r) => categoryForProgram(r.program) === active)),
    [active]
  );

  return (
  <div className="min-h-screen bg-slate-950">
    <Helmet>
      <title>Client Reviews & Experiences | TCUBE FITNESS™</title>
      <meta
        name="description"
        content="Real, consent-based experiences from TCUBE FITNESS clients on doctor-led diabetes management, sustainable weight loss and nutrition. Individual results vary."
      />
      <link rel="canonical" href="https://tcubefitness.com/reviews" />
    </Helmet>

    <SEOSchema
      type="webpage"
      name="Client Reviews & Experiences | TCUBE FITNESS™"
      description="Genuine, consent-based client experiences with doctor-led nutrition and fitness at TCUBE FITNESS. Individual results vary."
      url="/reviews"
      breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Reviews', path: '/reviews' }]}
    />

    <Header />

    <main id="main-content" className="pt-28 sm:pt-32 pb-20" data-testid="reviews-page">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Hero */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-400 text-sm font-medium mb-6">
            <Star className="w-4 h-4 fill-amber-400" aria-hidden="true" />
            Client Experiences
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
            Real Stories from{' '}
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Real Clients</span>
          </h1>
          <p className="text-slate-300 text-base sm:text-lg">
            These are genuine experiences shared by our clients with their consent. We keep them exactly as told,
            because honest, doctor-led progress speaks for itself.
          </p>
        </div>

        {/* Rating summary */}
        <div className="max-w-3xl mx-auto mb-8" data-testid="reviews-rating-summary">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 bg-slate-900/60 border border-amber-500/20 rounded-2xl px-6 py-5">
            <div className="flex items-center gap-3">
              <span className="text-4xl font-bold text-white leading-none">5.0</span>
              <div>
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} className="w-4 h-4 text-amber-400 fill-amber-400" aria-hidden="true" />
                  ))}
                </div>
                <div className="text-slate-400 text-xs mt-1">Average rating</div>
              </div>
            </div>
            <div className="hidden sm:block w-px h-10 bg-slate-700" aria-hidden="true"></div>
            <p className="text-slate-300 text-sm text-center sm:text-left">
              All <span className="text-white font-semibold">{clientReviews.length} of {clientReviews.length}</span> client reviews are rated{' '}
              <span className="text-amber-400 font-semibold">5 stars</span>.
            </p>
          </div>
        </div>

        {/* Consent & results disclaimer */}
        <div className="max-w-3xl mx-auto mb-10">
          <div className="flex items-start gap-3 bg-slate-900/50 border border-slate-800 rounded-2xl p-4 sm:p-5">
            <ShieldCheck className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
              <span className="text-slate-300 font-medium">Please note:</span> The experiences below are shared
              voluntarily and with consent. Individual results vary based on personal medical factors, consistency
              and adherence, and are not guaranteed. Nothing here is medical advice or a substitute for care from
              your treating physician. Any medication changes are made only by a client's own doctor.
            </p>
          </div>
        </div>

        {/* Category filter */}
        <div className="max-w-5xl mx-auto mb-8 flex flex-wrap items-center justify-center gap-2.5" data-testid="reviews-filter-bar">
          {CATEGORIES.map((cat) => {
            const count = cat === 'All' ? clientReviews.length : clientReviews.filter((r) => categoryForProgram(r.program) === cat).length;
            if (count === 0) return null;
            const isActive = active === cat;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setActive(cat)}
                aria-pressed={isActive}
                data-testid={`reviews-filter-${cat.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                  isActive
                    ? 'bg-blue-600 border-blue-500 text-white shadow-lg shadow-blue-500/20'
                    : 'bg-slate-900/60 border-slate-700 text-slate-300 hover:border-slate-500 hover:text-white'
                }`}
              >
                {cat}
                <span className={`text-xs ${isActive ? 'text-blue-100' : 'text-slate-500'}`}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* Reviews grid */}
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6 items-stretch" data-testid="reviews-grid">
          {filtered.map((review, i) => (
            <ReviewCard key={review.id} review={review} index={i} />
          ))}
        </div>

        {/* CTA */}
        <div className="max-w-3xl mx-auto text-center mt-16 bg-gradient-to-br from-blue-900/30 to-cyan-900/30 border border-blue-500/30 rounded-3xl p-8 sm:p-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Ready to begin your own journey?</h2>
          <p className="text-slate-300 mb-6 max-w-xl mx-auto">
            Book a free consultation with our medical team and start with a plan built around your health, not a template.
          </p>
          <Link
            to="/book-consultation?from=reviews"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white px-7 py-3.5 rounded-full font-bold transition-all shadow-lg shadow-blue-500/25"
            data-testid="reviews-cta"
          >
            Book a Free Consultation <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </main>

    <Footer />
  </div>
  );
};

export default ReviewsPage;
